import { Link } from "react-router-dom";
import { useT } from "@/contexts/I18nContext";
import { useSupportRequests } from "./useSupportRequests";
import { activeRequest, minutesRemaining } from "./lifecycle";

/**
 * What an institution is told while somebody is inside it.
 *
 * **For the whole window, on every screen.** An owner who approved something on
 * Tuesday and forgot has not consented on Thursday in any sense worth the word,
 * so the banner is in the shell rather than on a page they might not open.
 *
 * A pending request gets a quieter one. It is not access — nothing is open
 * until they decide — but a request sitting unanswered for a week is a support
 * problem nobody knows they have.
 */
export function SupportBanner() {
  const t = useT();
  const { requests } = useSupportRequests();
  const now = Date.now();

  const open = activeRequest(requests, now);
  const pending = requests.find((request) => request.status === "pending");

  if (open) {
    return (
      <div
        role="status"
        className="border-b border-warning bg-warning-soft px-4 py-3 text-sm"
      >
        <p className="text-ink">
          {t("support.banner.active", {
            who: open.requestedByEmail,
            minutes: minutesRemaining(open, now),
            reason: open.reason,
          })}
        </p>
        <p className="mt-1 text-ink-soft">
          {t("support.banner.readOnly")}
          <Link to="/support" className="ml-3 font-medium text-ink underline underline-offset-2">
            {t("support.banner.close")}
          </Link>
        </p>
      </div>
    );
  }

  if (pending) {
    return (
      <div role="status" className="border-b border-line bg-surface-muted px-4 py-3 text-sm">
        <p className="text-ink-soft">
          {t("support.banner.pending")}
          <Link to="/support" className="ml-3 font-medium text-ink underline underline-offset-2">
            {t("support.banner.decide")}
          </Link>
        </p>
      </div>
    );
  }

  return null;
}
